import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, ArrowLeft, Loader2, Zap, Shield, TrendingUp,
  Users, ChevronRight, Lock, DollarSign, Building2, BarChart3,
  Target, Award, Lightbulb, CheckCircle, Info
} from 'lucide-react';
import axios from 'axios';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/context/AuthContext';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTORS = [
  { value: 'technology', label: 'Technology' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'retail', label: 'Retail' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'energy', label: 'Energy' },
  { value: 'finance', label: 'Finance' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'agriculture', label: 'Agriculture' },
  { value: 'education', label: 'Education' },
  { value: 'hospitality', label: 'Hospitality' },
  { value: 'construction', label: 'Construction' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'professional_services', label: 'Professional Services' },
  { value: 'food_beverage', label: 'Food & Beverage' },
  { value: 'other', label: 'Other' },
];

const TIER_COLORS = {
  'Instant-Match': { bg: 'from-green-500 to-emerald-500', text: 'text-green-400', ring: '#22c55e' },
  'Market-Ready': { bg: 'from-blue-500 to-cyan-500', text: 'text-blue-400', ring: '#3b82f6' },
  'Competitive': { bg: 'from-yellow-500 to-amber-500', text: 'text-yellow-400', ring: '#eab308' },
  'Developing': { bg: 'from-orange-500 to-red-500', text: 'text-orange-400', ring: '#f97316' },
  'Building': { bg: 'from-gray-500 to-gray-600', text: 'text-gray-400', ring: '#6b7280' },
};

function ScoreGauge({ score, tier, animate }) {
  const colors = TIER_COLORS[tier] || TIER_COLORS['Building'];
  const circumference = 2 * Math.PI * 90;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="relative w-56 h-56 mx-auto" data-testid="score-gauge">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 200 200">
        <circle cx="100" cy="100" r="90" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="12" />
        <motion.circle
          cx="100" cy="100" r="90" fill="none"
          stroke={colors.ring}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: animate ? offset : circumference }}
          transition={{ duration: 1.5, ease: 'easeOut', delay: 0.3 }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          className="text-5xl font-bold text-white"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: animate ? 1 : 0, scale: animate ? 1 : 0.5 }}
          transition={{ duration: 0.5, delay: 1 }}
          data-testid="score-value"
        >
          {Math.round(score)}
        </motion.span>
        <motion.span
          className={`text-sm font-semibold ${colors.text} mt-1`}
          initial={{ opacity: 0 }}
          animate={{ opacity: animate ? 1 : 0 }}
          transition={{ delay: 1.2 }}
          data-testid="score-tier"
        >
          {tier}
        </motion.span>
      </div>
    </div>
  );
}

function ComponentBar({ label, icon: Icon, score, color, details }) {
  return (
    <div className="space-y-1.5" data-testid={`component-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-2 text-gray-400">
          <Icon className={`w-3.5 h-3.5 ${color}`} />{label}
        </span>
        <span className="text-white font-medium">{Math.round(score)}/100</span>
      </div>
      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
        <motion.div
          className={`h-full rounded-full bg-gradient-to-r ${TIER_COLORS[score >= 85 ? 'Instant-Match' : score >= 70 ? 'Market-Ready' : score >= 55 ? 'Competitive' : score >= 40 ? 'Developing' : 'Building'].bg}`}
          initial={{ width: 0 }}
          animate={{ width: `${score}%` }}
          transition={{ duration: 1, delay: 0.5 }}
        />
      </div>
      {Object.entries(details).filter(([k]) => k !== 'tip').map(([key, val]) => (
        <p key={key} className="text-[11px] text-gray-600 pl-5">{val}</p>
      ))}
    </div>
  );
}

function StepIndicator({ current, total }) {
  return (
    <div className="flex items-center gap-2 justify-center mb-6">
      {Array.from({ length: total }, (_, i) => (
        <div key={i} className={`h-1.5 rounded-full transition-all ${i <= current ? 'bg-blue-500 w-8' : 'bg-white/10 w-4'}`} />
      ))}
    </div>
  );
}

export default function MatchScorePage() {
  const { isAuthenticated, token } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const resultRef = useRef(null);

  const [form, setForm] = useState({
    loan_amount: 25000,
    loan_purpose: '',
    sector: 'other',
    annual_income: null,
    years_in_business: null,
    has_collateral: false,
    previous_loans_repaid: 0,
    previous_defaults: 0,
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const submit = async () => {
    setLoading(true);
    try {
      const endpoint = isAuthenticated ? '/api/match-score/calculate' : '/api/match-score/simulate';
      const headers = isAuthenticated && token ? { Authorization: `Bearer ${token}` } : {};
      const payload = {
        ...form,
        annual_income: form.annual_income || null,
        years_in_business: form.years_in_business || null,
      };
      const res = await axios.post(`${API}${endpoint}`, payload, { headers });
      setResult(res.data);
      setStep(3);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e) {
      console.error('Match-Score error:', e);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(0);
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 max-w-3xl mx-auto">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <Badge className="bg-blue-500/10 text-blue-400 mb-4" data-testid="simulator-badge">Match-Score Simulator</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold mb-3" data-testid="simulator-title">
            Discover What Your{' '}
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Credit Is Worth
            </span>
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto text-base">
            Stop accepting the first rate a bank offers. On KOJIRU, lenders compete to fund you — driving your rate down and revealing the true monetary value of your credit.
          </p>
        </motion.div>

        <AnimatePresence mode="wait">
          {step < 3 ? (
            <motion.div
              key={`step-${step}`}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="bg-[#0D0D0D] border-white/5">
                <CardContent className="p-6 sm:p-8">
                  <StepIndicator current={step} total={3} />

                  {/* Step 0: Loan basics */}
                  {step === 0 && (
                    <div className="space-y-5" data-testid="step-0">
                      <div>
                        <h2 className="text-lg font-semibold text-white mb-1">Loan Details</h2>
                        <p className="text-sm text-gray-500">Tell us what you need. Lenders will compete to offer you the best rate.</p>
                      </div>

                      <div className="space-y-1">
                        <Label className="text-gray-400">Loan Amount</Label>
                        <div className="flex items-center gap-3">
                          <DollarSign className="w-4 h-4 text-gray-500" />
                          <Input
                            type="number"
                            value={form.loan_amount}
                            onChange={e => update('loan_amount', Number(e.target.value))}
                            className="bg-white/5 border-white/10 text-white"
                            data-testid="input-loan-amount"
                          />
                        </div>
                        <p className="text-xs text-gray-600 pl-7">${form.loan_amount.toLocaleString()}</p>
                      </div>

                      <div className="space-y-1">
                        <Label className="text-gray-400">Loan Purpose</Label>
                        <Input
                          placeholder="e.g., Equipment purchase, Working capital..."
                          value={form.loan_purpose}
                          onChange={e => update('loan_purpose', e.target.value)}
                          className="bg-white/5 border-white/10 text-white"
                          data-testid="input-loan-purpose"
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-gray-400">Business Sector</Label>
                        <Select value={form.sector} onValueChange={v => update('sector', v)}>
                          <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="input-sector">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {SECTORS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>

                      <Button
                        onClick={() => setStep(1)}
                        disabled={!form.loan_purpose || form.loan_amount <= 0}
                        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 mt-2"
                        data-testid="next-step-1"
                      >
                        Next: Financial Profile <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  )}

                  {/* Step 1: Financial profile */}
                  {step === 1 && (
                    <div className="space-y-5" data-testid="step-1">
                      <div>
                        <h2 className="text-lg font-semibold text-white mb-1">Financial Profile</h2>
                        <p className="text-sm text-gray-500">Optional — more data = more accurate score.</p>
                      </div>

                      <div className="space-y-1">
                        <Label className="text-gray-400">Annual Income (optional)</Label>
                        <Input
                          type="number"
                          placeholder="e.g., 75000"
                          value={form.annual_income || ''}
                          onChange={e => update('annual_income', e.target.value ? Number(e.target.value) : null)}
                          className="bg-white/5 border-white/10 text-white"
                          data-testid="input-annual-income"
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-gray-400">Years in Business (optional)</Label>
                        <Input
                          type="number"
                          placeholder="e.g., 3"
                          value={form.years_in_business || ''}
                          onChange={e => update('years_in_business', e.target.value ? Number(e.target.value) : null)}
                          className="bg-white/5 border-white/10 text-white"
                          data-testid="input-years"
                        />
                      </div>

                      <div className="flex items-center justify-between p-3 bg-white/[0.02] rounded-lg border border-white/5">
                        <Label className="text-gray-400">Do you have collateral?</Label>
                        <Switch
                          checked={form.has_collateral}
                          onCheckedChange={v => update('has_collateral', v)}
                          data-testid="input-collateral"
                        />
                      </div>

                      <div className="flex gap-3">
                        <Button variant="ghost" onClick={() => setStep(0)} className="flex-1" data-testid="back-step-0">
                          <ArrowLeft className="w-4 h-4 mr-2" />Back
                        </Button>
                        <Button
                          onClick={() => setStep(2)}
                          className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600"
                          data-testid="next-step-2"
                        >
                          Next: Track Record <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Track record */}
                  {step === 2 && (
                    <div className="space-y-5" data-testid="step-2">
                      <div>
                        <h2 className="text-lg font-semibold text-white mb-1">Track Record</h2>
                        <p className="text-sm text-gray-500">Your lending history builds trust.</p>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-gray-400">Previous Loans Successfully Repaid</Label>
                        <Slider
                          value={[form.previous_loans_repaid]}
                          onValueChange={v => update('previous_loans_repaid', v[0])}
                          min={0} max={10} step={1}
                          data-testid="input-repaid"
                        />
                        <p className="text-xs text-gray-500 text-center">{form.previous_loans_repaid} loans</p>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-gray-400">Previous Defaults</Label>
                        <Slider
                          value={[form.previous_defaults]}
                          onValueChange={v => update('previous_defaults', v[0])}
                          min={0} max={5} step={1}
                          data-testid="input-defaults"
                        />
                        <p className="text-xs text-gray-500 text-center">{form.previous_defaults} defaults</p>
                      </div>

                      {isAuthenticated && (
                        <div className="flex items-center gap-2 bg-blue-500/5 border border-blue-500/20 rounded-lg p-3">
                          <CheckCircle className="w-4 h-4 text-blue-400 shrink-0" />
                          <p className="text-xs text-blue-400">Logged in — your real platform data will boost your score.</p>
                        </div>
                      )}

                      <div className="flex gap-3">
                        <Button variant="ghost" onClick={() => setStep(1)} className="flex-1" data-testid="back-step-1">
                          <ArrowLeft className="w-4 h-4 mr-2" />Back
                        </Button>
                        <Button
                          onClick={submit}
                          disabled={loading}
                          className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600"
                          data-testid="calculate-score"
                        >
                          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                          Calculate Match-Score
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            /* Results */
            <motion.div
              ref={resultRef}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-5"
              data-testid="results-section"
            >
              {/* Main score */}
              <Card className="bg-[#0D0D0D] border-white/5 overflow-hidden relative">
                {result.instant_match && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 via-emerald-400 to-green-500 animate-pulse" />
                )}
                <CardContent className="p-6 sm:p-8">
                  <ScoreGauge score={result.match_score} tier={result.tier} animate={true} />

                  {result.instant_match && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 1.5 }}
                      className="text-center mt-4"
                      data-testid="instant-match-badge"
                    >
                      <Badge className="bg-green-500/10 text-green-400 text-sm px-4 py-1.5">
                        <Zap className="w-4 h-4 mr-1.5" />Instant-Match Eligible
                      </Badge>
                    </motion.div>
                  )}

                  <div className="grid grid-cols-3 gap-3 mt-6">
                    <div className="text-center p-3 bg-white/[0.02] rounded-lg border border-white/5">
                      <p className="text-xs text-gray-500">Est. Rate via Bidding</p>
                      <p className="text-lg font-bold text-white">{result.estimated_rate_range.min}-{result.estimated_rate_range.max}%</p>
                      <p className="text-[10px] text-gray-600">{result.estimated_rate_range.label}</p>
                    </div>
                    <div className="text-center p-3 bg-white/[0.02] rounded-lg border border-white/5">
                      <p className="text-xs text-gray-500">Competing Lenders</p>
                      <p className="text-lg font-bold text-white">{result.lender_matches}</p>
                      <p className="text-[10px] text-gray-600">would bid on your loan</p>
                    </div>
                    <div className="text-center p-3 bg-white/[0.02] rounded-lg border border-white/5">
                      <p className="text-xs text-gray-500">Your Tier</p>
                      <p className={`text-lg font-bold ${TIER_COLORS[result.tier]?.text || 'text-white'}`}>{result.tier}</p>
                      <p className="text-[10px] text-gray-600">{result.match_score}/100</p>
                    </div>
                  </div>

                  {/* Savings highlight */}
                  {result.estimated_savings && result.estimated_savings.rate_advantage > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.8 }}
                      className="mt-5 p-4 rounded-xl bg-gradient-to-r from-green-500/5 to-emerald-500/5 border border-green-500/20"
                      data-testid="savings-highlight"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center shrink-0">
                          <DollarSign className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-green-400">
                            Save ~${result.estimated_savings.estimated_annual_savings.toLocaleString()}/year
                          </p>
                          <p className="text-xs text-gray-500">
                            Traditional banks average {result.estimated_savings.traditional_avg_apr}% APR.
                            Lender competition on KOJIRU could get you ~{result.estimated_savings.kutro_estimated_apr}% — a {result.estimated_savings.rate_advantage}% advantage.
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </CardContent>
              </Card>

              {/* Score breakdown */}
              <Card className="bg-[#0D0D0D] border-white/5">
                <CardContent className="p-6 space-y-4">
                  <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-blue-400" />Score Breakdown
                  </h3>
                  <ComponentBar label="Reliability (40%)" icon={Shield} score={result.r_score} color="text-blue-400" details={result.r_details} />
                  <ComponentBar label="Lender Appeal (30%)" icon={TrendingUp} score={result.y_score} color="text-green-400" details={result.y_details} />
                  <ComponentBar label="Volatility Buffer (20%)" icon={Target} score={result.v_score} color="text-yellow-400" details={result.v_details} />
                  <ComponentBar label="Social Proof (10%)" icon={Users} score={result.s_score} color="text-purple-400" details={result.s_details} />
                </CardContent>
              </Card>

              {/* AI Improvement tips */}
              {result.improvement_tips && (
                <Card className="bg-[#0D0D0D] border-blue-500/20" data-testid="improvement-tips">
                  <CardContent className="p-6">
                    <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
                      <Lightbulb className="w-4 h-4 text-yellow-400" />AI Improvement Tips
                    </h3>
                    <div className="text-sm text-gray-400 leading-relaxed whitespace-pre-line">
                      {result.improvement_tips}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Formula explainer */}
              <Card className="bg-white/[0.01] border-white/5">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Info className="w-4 h-4 text-gray-500" />
                    <span className="text-xs font-medium text-gray-500">How It's Calculated</span>
                  </div>
                  <p className="text-xs text-gray-600 font-mono">
                    Ms = (0.40 x R) + (0.30 x Y) + (0.20 x V) + (0.10 x S)
                  </p>
                  <p className="text-[11px] text-gray-600 mt-2">
                    R = Reliability & Trust | Y = Lender Appeal | V = Volatility Buffer | S = Social Proof
                  </p>
                  <p className="text-[11px] text-gray-500 mt-1">
                    Your rate isn't set by you — it's determined by lenders competing to fund you.
                  </p>
                </CardContent>
              </Card>

              {/* CTAs */}
              <div className="flex gap-3">
                <Button variant="outline" onClick={reset} className="flex-1 border-white/10" data-testid="recalculate-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />Recalculate
                </Button>
                {!isAuthenticated ? (
                  <Link to="/register" className="flex-1">
                    <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600" data-testid="signup-cta">
                      Monetize Your Credit <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                ) : (
                  <Link to="/dashboard" className="flex-1">
                    <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600" data-testid="dashboard-cta">
                      Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bottom social proof */}
        {step < 3 && (
          <div className="mt-8 text-center space-y-3">
            <p className="text-xs text-gray-600">No login required. Your data is never stored. Rates determined by lender competition, not algorithms.</p>
            <div className="flex items-center justify-center gap-6 text-xs text-gray-500">
              <span className="flex items-center gap-1"><Shield className="w-3 h-3" />5-Layer Security</span>
              <span className="flex items-center gap-1"><Zap className="w-3 h-3" />AI-Powered</span>
              <span className="flex items-center gap-1"><Lock className="w-3 h-3" />Privacy-First</span>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
